import { useQuery } from '@apollo/client'
import { Separator } from '@farol-ds/react'
import { withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import {
  buildExperimentContext,
  buildMetricsProperties,
  DocPopoverElement,
  DocSearch,
  DocSearchElement,
  Docview,
  FEATURE_CODES,
  getDocumentStatusCode,
  GROWTH_BOOK_FEATURE_FLAGS,
  isGoogleBotAgent,
  JUSBRASIL_HOTJAR_SITE_ID,
  mapToGA4ComponentName,
  mapToGA4FeatureName,
  MetricsProperties,
  printStyles,
  RecommendedDocuments,
  redirectDocumentUrl,
  shouldConsumeViewCredits,
  useConsumeCredits,
  useDwellTime,
  useMetrics,
  ViewLockContext,
  ViewLockWrapper,
} from '@jusbrasil-web/docview/core'
import { SetNonNullableQueryRootProps } from '@jusbrasil-web/shared-apollo-client/utilities'
import { AppBar } from '@jusbrasil-web/shared-appbar'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { withGrowthBook } from '@jusbrasil-web/shared-growthbook'
import { hotjarEvent, HotjarScript } from '@jusbrasil-web/shared-hotjar-script'
import { ServerSidePersistentAlertBanner } from '@jusbrasil-web/shared-persistent-alert-banner'
import { QualtricsScriptLoader } from '@jusbrasil-web/shared-qualtrics-script'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useEffect, useRef } from 'react'
import UAParser from 'ua-parser-js'

import {
  Comments,
  ModelDocument,
  ModelMetaTags,
  ModelToolbar,
  ModerationBanner,
  PublicationAppbar,
  PublicationHeader,
  PublicationSidebar,
} from '../../components'
import { getActiveExperiments } from '../../experiment'
import { extractFromUrl } from '../../utils'
import styles from '../publications.module.scss'
import { ModelRootQuery, ModelRootQueryVariables } from './__generated__/query.graphql'
import { ModelRoot } from './query'

export type ModelPageProps = {
  variables: ModelRootQueryVariables
  metricsProperties: MetricsProperties
  disableTouchFeatures?: boolean
  httpError?: true
  shouldConsumeViewCredits?: boolean
  isGoogleBotAgent?: boolean
}

type ModelRootQueryPage = SetNonNullableQueryRootProps<ModelRootQuery, 'document'>

const eventData = {
  app_id: 'web-docview',
  from_component: 'docview/ModelPage',
}
const RESOURCE_ID_VALIDATION_REGEX = /^\w+:\d+$/

export function ModelPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const {
    variables,
    metricsProperties,
    disableTouchFeatures,
    httpError,
    shouldConsumeViewCredits,
    isGoogleBotAgent,
  } = props

  const contentRef = useRef<HTMLDivElement>(null)
  const docSearchRef = useRef<DocSearchElement>(null)
  const docPopoverRef = useRef<DocPopoverElement>(null)
  const { ref: dispatcherRef, sendMetrics, sendCopyEvents } = useMetrics<HTMLDivElement>()
  const { sendGA4Event } = useGA4Dispatcher()

  const { data } = useQuery<ModelRootQueryPage>(ModelRoot, { variables })
  const { document, viewer = null, persistentAlertBannerViewer = null } = data?.root || {}
  const hasPaidPlan = !!viewer?.hasPaidPlan

  useConsumeCredits(shouldConsumeViewCredits, document)
  useDwellTime(document, sendMetrics, metricsProperties)

  // This hook should be run only once per page, that's why docId should be its only dependency
  useEffect(() => {
    sendMetrics('Doc.Viewed', metricsProperties)

    hotjarEvent('event', 'ExperimentModelFarolRollout')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.docId])

  if (httpError || !document) return null

  const { canCopyContent } = document
  const sendGA4RecommendedDocumentsCopyMetrics = (resourceId: string | null | undefined) => {
    if (!resourceId || !RESOURCE_ID_VALIDATION_REGEX.test(resourceId)) return
    const artifact = resourceId?.split(':')[0]
    sendGA4Event(
      new GA4FeatureUseEvent({
        featureClass: FEATURE_CLASS.COPY_CONTENT,
        featureName: mapToGA4FeatureName(FEATURE_CLASS.COPY_CONTENT, artifact),
        componentClass: COMPONENT_CLASS.RELATED_DOCUMENTS,
        componentName: mapToGA4ComponentName(COMPONENT_CLASS.RELATED_DOCUMENTS, artifact),
        resourceId: resourceId,
        successfulAttempt: !!canCopyContent,
        requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
      })
    )
  }

  const handleCopy = (ev: React.ClipboardEvent<HTMLDivElement>) => {
    const target = ev.target as Element
    const fromComponentParent = target.closest('[data-text-from-component]')
    const fromComponent = fromComponentParent?.getAttribute('data-text-from-component')
    sendGA4RecommendedDocumentsCopyMetrics(fromComponent)
    sendCopyEvents(metricsProperties)
  }

  const handleDocumentDownloadFrom =
    (componentClass: COMPONENT_CLASS) => (successfulAttempt: boolean) => {
      sendGA4Event(
        new GA4FeatureUseEvent({
          featureClass: FEATURE_CLASS.DOCUMENT_DOWNLOAD,
          featureName: FEATURE_NAME.DOWNLOAD_MODEL_CONTENT,
          selectedOption: SELECTED_OPTION.DOWNLOAD_BUTTON,
          resourceId: document.boxResourceId,
          requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
          componentClass,
          successfulAttempt,
        })
      )
    }

  const onLoadMoreComments = () => docPopoverRef.current?.refreshElementNodes()

  return (
    <AppBar.Provider>
      <ServerSidePersistentAlertBanner persistentAlertBannerViewer={persistentAlertBannerViewer} />
      <Docview
        document={document}
        viewer={viewer}
        handleCopy={handleCopy}
        metricsProperties={metricsProperties}
        canCopyContent={!!canCopyContent}
        dispatcherRef={dispatcherRef}
        contentRef={dispatcherRef}
        docPopoverRef={docPopoverRef}
        disableTouchFeatures={!!disableTouchFeatures}
        topbarCurrentNavKey="MODELOS"
        metaTags={<ModelMetaTags document={document} />}
        afterTopbar={
          hasPaidPlan && (
            <DocSearch
              contentRef={contentRef}
              ref={docSearchRef}
              slug={document.slug || undefined}
            />
          )
        }
        header={
          <>
            <ModerationBanner document={document} viewer={viewer} />
            <PublicationHeader
              document={document}
              viewer={viewer}
              toolbar={
                <ModelToolbar
                  document={document}
                  viewer={viewer}
                  docSearchRef={docSearchRef}
                  docContentRef={contentRef}
                  onDocumentDownload={handleDocumentDownloadFrom(COMPONENT_CLASS.DOCUMENT_HEADER)}
                  componentClass={COMPONENT_CLASS.DOCUMENT_HEADER}
                  ga4DocumentSaveEventData={{ componentClass: COMPONENT_CLASS.DOCUMENT_HEADER }}
                />
              }
            />
            <AppBar.StickyTrigger />
          </>
        }
        asideContent={<PublicationSidebar document={document} />}
        mainContent={
          <ViewLockContext
            viewer={viewer}
            document={document}
            featureContext={{
              featureCode: FEATURE_CODES.VIEW_LR_CONTENT,
              canUseFeature: document?.canViewLrContent ?? false,
            }}
          >
            <ViewLockWrapper
              document={document}
              viewer={viewer}
              eventData={eventData}
              maxHeight="500px"
              isGooglebotAgent={isGoogleBotAgent}
            >
              <ModelDocument document={document} viewer={viewer} contentRef={contentRef} />
            </ViewLockWrapper>
          </ViewLockContext>
        }
        relatedContent={
          <>
            <RecommendedDocuments document={document} />
            {document.recommendations.length > 0 && (
              <Separator
                className={classNames(
                  printStyles.unprintable,
                  styles.recommendedDocumentsSeparator
                )}
              />
            )}
            <Comments
              document={document}
              viewer={viewer}
              onLoadMore={onLoadMoreComments}
              className={printStyles.unprintable}
            />
          </>
        }
        endContent={
          <PublicationAppbar
            document={document}
            className={printStyles.unprintable}
            toolbar={
              <ModelToolbar
                disableRenderMetrics
                document={document}
                viewer={viewer}
                docSearchRef={docSearchRef}
                docContentRef={contentRef}
                onDocumentDownload={handleDocumentDownloadFrom(COMPONENT_CLASS.DOCUMENT_TOPBAR)}
                componentClass={COMPONENT_CLASS.DOCUMENT_TOPBAR}
                ga4DocumentSaveEventData={{ componentClass: COMPONENT_CLASS.DOCUMENT_TOPBAR }}
              />
            }
          />
        }
      />

      <HotjarScript hjid={JUSBRASIL_HOTJAR_SITE_ID} />
      <QualtricsScriptLoader viewer={viewer} />
    </AppBar.Provider>
  )
}

const getServerSideProps: GetServerSideProps<ModelPageProps> = async (ctx) => {
  const { parts } = ctx.query
  if (!parts) {
    return {
      notFound: true,
    }
  }
  const { docId } = extractFromUrl(Array.isArray(parts) ? parts : [parts])
  if (!docId) {
    return {
      notFound: true,
    }
  }

  const variables: ModelRootQueryVariables = {
    docId,
    isNewsPage: false,
  }
  const { client, withApolloProps } = withApollo(ctx)
  const { client: growthBookClient, withGrowthBookProps } = await withGrowthBook(ctx)

  const featureFlags = Object.values(GROWTH_BOOK_FEATURE_FLAGS).flatMap(({ name, values }) => {
    const featureValue = growthBookClient.getFeatureValue(name, '')
    return values.includes(featureValue) ? { name, value: featureValue } : []
  })

  const { data } = await client.query<ModelRootQuery>({
    query: ModelRoot,
    variables,
  })
  const { document, viewer } = data?.root ?? {}
  if (!document) {
    return {
      notFound: true,
    }
  }

  const experimentContext = await buildExperimentContext(ctx, getActiveExperiments())
  const metricsProperties = buildMetricsProperties(
    ctx,
    document,
    viewer,
    experimentContext,
    featureFlags
  )

  const status = getDocumentStatusCode(ctx.req, document, viewer)
  if (status !== 200) {
    ctx.res.statusCode = status
    return {
      props: {
        variables,
        metricsProperties,
        httpError: true,
      },
    }
  }

  const redirect = document.url && redirectDocumentUrl(ctx, document.url)
  if (redirect) {
    return { redirect }
  }

  const ua = UAParser(ctx.req.headers['user-agent'])
  const disableTouchFeatures = ua.device.type === 'mobile' || ua.device.type === 'tablet'

  return {
    props: {
      ...withApolloProps(),
      ...withGrowthBookProps(),
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      variables,
      metricsProperties,
      disableTouchFeatures,
      shouldConsumeViewCredits: shouldConsumeViewCredits(ctx.req, viewer),
      isGooglebotAgent: isGoogleBotAgent(ctx.req),
      persistentAlertBannerViewer: data?.root?.persistentAlertBannerViewer,
      featureFlags,
    },
  }
}

export const ModelPageExports = { getServerSideProps }
